import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, RotateCcw } from 'lucide-react';
import ErrorReportButton from '@/components/error/ErrorReportButton';
import { Sentry } from '@/lib/sentry';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  sentryEventId: string | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, sentryEventId: null };
  }

  static getDerivedStateFromError(error: Error): Pick<ErrorBoundaryState, 'hasError' | 'error'> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    Sentry.flush(2000).then(() => {
      const eventId = Sentry.lastEventId();
      this.setState({ errorInfo, sentryEventId: eventId ?? null });
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, sentryEventId: null });
  };

  handleReportCrash = () => {
    if (this.state.sentryEventId) {
      Sentry.showReportDialog({ eventId: this.state.sentryEventId });
    }
  };

  render() {
    if (this.state.hasError) {
      const additionalContext = {
        componentStack: this.state.errorInfo?.componentStack,
        errorBoundary: true,
      };

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-slate-200 p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Something went wrong</h2>
            <p className="text-slate-500 mb-6">
              We&apos;re sorry, but an unexpected error occurred. You can try again or reload the game.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-3 bg-red-50 text-red-800 text-xs text-left overflow-auto max-h-32 rounded border border-red-100">
                {this.state.error.toString()}
              </div>
            )}

            <div className="mb-6">
              {this.state.sentryEventId ? (
                <Button
                  onClick={this.handleReportCrash}
                  variant="outline"
                  size="default"
                  className="w-full"
                >
                  Report This Error
                </Button>
              ) : (
                <ErrorReportButton
                  feature="ErrorBoundary"
                  error={this.state.error ?? undefined}
                  additionalContext={additionalContext}
                  variant="button"
                  size="default"
                  className="w-full"
                />
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={this.handleRetry}
                variant="outline"
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={this.handleReload}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Game
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
